import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Card,
  CardBody,
  Badge,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Spinner,
  Center,
} from '@chakra-ui/react'
import { useAuth } from '@/contexts/AuthContext'
import { Organization } from '@/types'
import apiService from '@/services/api'

interface InvitationData {
  id: number
  email: string
  role: string
  organization: Organization
  expires_at: string
  status: 'pending' | 'accepted' | 'expired'
}

export default function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { loginWithToken } = useAuth()
  const toast = useToast()

  const [invitation, setInvitation] = useState<InvitationData | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (token) {
      loadInvitation()
    } else {
      setError('Invalid invitation link')
      setLoading(false)
    }
  }, [token])

  const loadInvitation = async () => {
    try {
      setLoading(true)
      const data = await apiService.getInvitation(token!)
      setInvitation(data)

      if (data.status === 'expired' || new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired')
      } else if (data.status === 'accepted') {
        setError('This invitation has already been accepted')
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setError('Invitation not found or has expired')
      } else {
        setError('Failed to load invitation details')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value })
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setFormErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAcceptInvitation = async () => {
    if (!validateForm()) return

    try {
      setAccepting(true)
      const response = await apiService.acceptInvitation(token!, {
        name: formData.name,
        password: formData.password,
      })

      // Automatically log the user in
      loginWithToken(response.token, response.user, response.organization)

      toast({
        title: 'Welcome!',
        description: `You have successfully joined ${response.organization.name}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      // Navigate to dashboard
      navigate('/dashboard')
    } catch (error: any) {
      toast({
        title: 'Error accepting invitation',
        description: error.response?.data?.message || 'Failed to accept invitation',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setAccepting(false)
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'purple'
      case 'editor':
        return 'blue'
      case 'viewer':
        return 'green'
      default:
        return 'gray'
    }
  }

  if (loading) {
    return (
      <Container maxW="md" py={12}>
        <Center>
          <VStack spacing={4}>
            <Spinner size="lg" />
            <Text>Loading invitation...</Text>
          </VStack>
        </Center>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="md" py={12}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    )
  }

  if (!invitation) {
    return (
      <Container maxW="md" py={12}>
        <Alert status="error">
          <AlertIcon />
          Invitation not found
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="md" py={12}>
      <VStack spacing={6}>
        <Box textAlign="center">
          <Heading size="lg" mb={2}>
            Join Organization
          </Heading>
          <Text color="gray.600">You've been invited to join {invitation.organization.name}</Text>
        </Box>

        <Card w="full">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Invitation Details
                </Text>
                <VStack spacing={2} align="stretch">
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Organization
                    </Text>
                    <Text fontWeight="medium">{invitation.organization.name}</Text>
                    {invitation.organization.description && (
                      <Text fontSize="sm" color="gray.600">
                        {invitation.organization.description}
                      </Text>
                    )}
                  </Box>

                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Your Role
                    </Text>
                    <Badge colorScheme={getRoleBadgeColor(invitation.role)}>
                      {invitation.role}
                    </Badge>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Email
                    </Text>
                    <Text>{invitation.email}</Text>
                  </Box>
                </VStack>
              </Box>

              <Box pt={4} borderTop="1px" borderColor="gray.200">
                <Text fontWeight="medium" mb={4}>
                  Complete Your Registration
                </Text>

                <VStack spacing={4}>
                  <FormControl isRequired isInvalid={!!formErrors.name}>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                    />
                    <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!formErrors.password}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Choose a secure password"
                    />
                    <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!formErrors.confirmPassword}>
                    <FormLabel>Confirm Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm your password"
                    />
                    <FormErrorMessage>{formErrors.confirmPassword}</FormErrorMessage>
                  </FormControl>

                  <Button
                    colorScheme="blue"
                    size="lg"
                    w="full"
                    onClick={handleAcceptInvitation}
                    isLoading={accepting}
                    loadingText="Joining..."
                  >
                    Accept Invitation & Join
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        <Text fontSize="sm" color="gray.500" textAlign="center">
          By accepting this invitation, you agree to join {invitation.organization.name} with{' '}
          {invitation.role} privileges.
        </Text>
      </VStack>
    </Container>
  )
}
